import "./super.scss";

import ScrollView from "devextreme-react/scroll-view";
import { run } from "effection";
import React, { useEffect, useMemo, useRef } from "react";

import {
  DirInfo,
  FormRemoteKey,
  GroupEnvironment,
  GroupsTree,
  OrgFormData,
  OrgNewFormData,
} from "@app/components";
import {
  Case,
  CaseElse,
  Switch,
  useReverseRoute,
  useWindowDimensions,
} from "@app/contexts";
import {
  canGenerateTemporaryToken,
  adoptHostedOnboarding,
  clearHostedOnboardingSelection,
  listHostedOnboardings,
  pollHostedOnboarding,
  selectAllGroupsList,
  selectCurrentGroupsTreeList,
  selectHostedOnboardings,
  selectHostedOnboardingById,
  selectedGroupData,
  setGroupTreeList,
  suGroups,
  useAppDispatch,
  useAppSelector,
} from "@app/state";
import * as appConst from "@app/state/constants";
import { buildGroupsTreeList } from "@app/state/units/ui/groupstree";
import { useFxDispatch, useFxSelector } from "@app/statefx";
import { schema } from "@app/statefx/schema";

import { SwsBuilder } from "./SwsBuilder";

export const SuperAdminGroups = () => {
  const { routeInit } = useReverseRoute("swsadmin/groups");
  const dispatch = useAppDispatch();
  const fxDispatch = useFxDispatch();
  const theCurrentGroup = useAppSelector(selectCurrentGroupsTreeList);
  const groups = useAppSelector(selectAllGroupsList);
  const onboardings = useFxSelector(selectHostedOnboardings);
  const createdOnboardingId = useFxSelector(
    schema.hostedOnboardingSelection.select
  );
  const selectedGroup = useAppSelector((state) =>
    selectedGroupData(state, theCurrentGroup?.id)
  );
  const selectedOnboarding = useFxSelector((state) =>
    selectHostedOnboardingById(state, theCurrentGroup?.ptlOnboardingId)
  );
  const treeRows = useMemo(
    () => buildGroupsTreeList(groups, onboardings),
    [groups, onboardings]
  );
  const refreshedReady = useRef<string>("");
  const { drawerHeight } = useWindowDimensions();

  useEffect(() => {
    fxDispatch(listHostedOnboardings());
  }, [fxDispatch]);

  useEffect(() => {
    if (!createdOnboardingId || theCurrentGroup?.ptlLifecycle !== "DRAFT") {
      return;
    }
    const row = treeRows.find(
      (item) => item.id === `onboarding:${createdOnboardingId}`
    );
    if (!row) return;
    dispatch(adoptHostedOnboarding(row));
    fxDispatch(clearHostedOnboardingSelection());
  }, [createdOnboardingId, dispatch, fxDispatch, theCurrentGroup, treeRows]);

  useEffect(() => {
    const id = theCurrentGroup?.ptlOnboardingId;
    if (!id || !selectedOnboarding) return;
    const task = run(function* () {
      yield* pollHostedOnboarding.run(pollHostedOnboarding({ id }));
    });
    return () => {
      void task.halt();
    };
  }, [selectedOnboarding?.id, selectedOnboarding?.status]);

  useEffect(() => {
    if (selectedOnboarding?.status !== "SERVER_READY") return;
    const refreshKey = `${selectedOnboarding.id}:${selectedOnboarding.updatedAt}`;
    if (refreshedReady.current !== refreshKey) {
      refreshedReady.current = refreshKey;
      dispatch(suGroups());
    }
    const finalized = treeRows.find(
      (row) =>
        row.ptlLifecycle === "FINALIZED" &&
        row.ptlDetail?.toUpperCase() === selectedOnboarding.perseed.toUpperCase()
    );
    if (finalized) dispatch(setGroupTreeList(finalized));
  }, [dispatch, selectedOnboarding, treeRows]);

  const showRemoteKey = canGenerateTemporaryToken(
    theCurrentGroup,
    selectedGroup
  );
  return (
    <div
      ref={routeInit}
      id={"groupsPage"}
      style={{ "--drawerHeight": `${drawerHeight}px` } as React.CSSProperties}
    >
      <div id={"groupsList"}>
        <GroupsTree />
      </div>
      <div id={"groupsContent"}>
        <ScrollView height={drawerHeight} showScrollbar="never">
          <div>
            <Switch value={theCurrentGroup?.id}>
              <Case when={[(x: number | undefined) => !x]}>
                <div>{"no selection"}</div>
              </Case>
              <CaseElse>
                <Switch value={theCurrentGroup?.ptlCfgPage}>
                  <Case when={appConst.GROUPS_PAGES.GROUP}>
                    <Switch value={theCurrentGroup?.ptlLifecycle}>
                      <Case
                        when={[
                          (lifecycle: string | undefined) =>
                            lifecycle === "DRAFT" ||
                            lifecycle === "PROVISIONING",
                        ]}
                      >
                        <OrgNewFormData
                          readOnly={
                            theCurrentGroup?.ptlLifecycle === "PROVISIONING" &&
                            selectedOnboarding?.status !==
                              "MANUAL_REVIEW_REQUIRED"
                          }
                        />
                      </Case>
                      <Case when="FINALIZED">
                        <OrgFormData />
                        {showRemoteKey ? <FormRemoteKey /> : null}
                      </Case>
                    </Switch>
                  </Case>
                  <Case when={appConst.GROUPS_PAGES.ENVIRONMENT}>
                    <div>{theCurrentGroup?.ptlParentText}</div>
                    <GroupEnvironment />
                  </Case>
                  <Case when={appConst.GROUPS_PAGES.DIRINFO}>
                    <div>{theCurrentGroup?.ptlParentText}</div>
                    <DirInfo />
                  </Case>
                  <Case when={appConst.GROUPS_PAGES.BUILDER}>
                    <div>{theCurrentGroup?.ptlParentText}</div>
                    <SwsBuilder />
                  </Case>
                </Switch>
              </CaseElse>
            </Switch>
            <div style={{ display: "none" }}>
              <Switch value={theCurrentGroup?.touched}>
                <Case when={[(x: boolean | undefined) => x === true]}>
                  <div>{"touched"}</div>
                </Case>
                <CaseElse>
                  <div>{"not touched"}</div>
                </CaseElse>
              </Switch>
            </div>
          </div>
        </ScrollView>
      </div>
    </div>
  );
};
